import SCALABLE_UNITS, { ScalableUnitConfig } from '../constants/scalableUnits';

// Qualitative units that should render as “A” instead of numeric
const QUALITATIVE_UNITS = new Set(['handful', 'pinch', 'to taste']);

/** Parse a fraction string or number ("1/2" -> 0.5) */
export function parseFraction(val: string | number): number {
  if (typeof val === 'number') return val;
  const str = val.trim();
  if (str.includes('/')) {
    const [num, den] = str.split('/').map(Number);
    return den ? num / den : NaN;
  }
  const parsed = parseFloat(str);
  return isNaN(parsed) ? NaN : parsed;
}

/**
 * Promote a raw amount through units defined in SCALABLE_UNITS and round.
 * Returns { amount, unit } where amount > 0 for rawAmt > 0.
 */
export function promoteAndRound(
  rawAmt: number,
  unitShort: string
): { amount: number; unit: string } {
  const cfg: ScalableUnitConfig | undefined = SCALABLE_UNITS[unitShort];
  const wasPositive = rawAmt > 0;

  // Non-scalable: simple rounding or half unit for <1
  if (!cfg) {
    if (rawAmt > 0 && rawAmt < 1) return { amount: 0.5, unit: unitShort };
    return { amount: Math.round(rawAmt), unit: unitShort };
  }

  // (A) Promote through thresholds
  let amt = rawAmt;
  let curUnit = unitShort;

  while (true) {
    const { promotionThreshold, promoteTo } = SCALABLE_UNITS[curUnit]!;
    if (!promoteTo || amt < promotionThreshold) break;
    amt /= promotionThreshold;
    curUnit = promoteTo;
  }

  // (B) Find rounding increment
  const finalCfg = SCALABLE_UNITS[curUnit]!;
  const rule = finalCfg.roundingRules.find(r => amt >= r.min && amt < r.max);
  const increment = rule?.increment ?? 1;

  // (C) Round
  let rounded: number;
  if (increment >= 1) {
    rounded = Math.round(amt / increment) * increment;
  } else {
    const decimals = increment.toString().split('.')[1]?.length ?? 0;
    rounded = parseFloat((Math.round(amt / increment) * increment).toFixed(decimals));
  }

  // (D) Ensure small positives don’t become zero
  if (rounded === 0 && wasPositive) {
    const smallest = finalCfg.roundingRules[0]?.increment ?? 0.5;
    rounded = smallest;
  }

  return { amount: rounded, unit: curUnit };
}

/**
 * Formats a quantity + unit into { amountStr, unitStr }.
 * Scales by servings, promotes/rounds, handles qualitative and fractions.
 */
export function scaledAmountParts(
  amount: string | number,
  baseServings: number,
  currentServings: number,
  unitShort: string
): { amountStr: string; unitStr: string } {
  // 1) Parse
  const parsed = parseFraction(amount);
  if (isNaN(parsed)) {
    return {
      amountStr: typeof amount === 'number' ? amount.toString() : amount,
      unitStr: unitShort,
    };
  }

  // 2) Scale by servings
  const factor = baseServings > 0 ? currentServings / baseServings : 1;
  const rawScaled = parsed * factor;

  // 3) Promote & round
  const { amount: finalAmt, unit: finalUnit } = promoteAndRound(rawScaled, unitShort);

  // 4) Qualitative units (handful, pinch, etc.)
  if (QUALITATIVE_UNITS.has(finalUnit)) {
    const amtStr = finalAmt === 1 ? 'A' : finalAmt.toString();
    return { amountStr: amtStr, unitStr: finalUnit };
  }

  // 5) Unicode fractions for cup/tbsp/tsp
  if (['cup', 'tbsp', 'tsp'].includes(finalUnit)) {
    const intPart = Math.floor(finalAmt);
    const frac = +(finalAmt - intPart).toFixed(2);
    const symbol =
      Math.abs(frac - 0.25) < 1e-6 ? '¼' :
      Math.abs(frac - 0.5)  < 1e-6 ? '½' :
      Math.abs(frac - 0.75) < 1e-6 ? '¾' : '';

    if (intPart > 0 && symbol) return { amountStr: `${intPart}${symbol}`, unitStr: finalUnit };
    if (intPart > 0) return { amountStr: `${intPart}`, unitStr: finalUnit };
    if (symbol)      return { amountStr: symbol, unitStr: finalUnit };
    return { amountStr: finalAmt.toString(), unitStr: finalUnit };
  }

  // 6) Non-scalable small fractions (½, ¼, ¾)
  if (!SCALABLE_UNITS[finalUnit] && finalAmt < 1) {
    if (Math.abs(finalAmt - 0.5) < 1e-6) return { amountStr: '½', unitStr: finalUnit };
    if (Math.abs(finalAmt - 0.25) < 1e-6) return { amountStr: '¼', unitStr: finalUnit };
    if (Math.abs(finalAmt - 0.75) < 1e-6) return { amountStr: '¾', unitStr: finalUnit };
  }

  // 7) Fallback numeric formatting
  let amtStr: string;
  if (Number.isInteger(finalAmt)) {
    amtStr = finalAmt.toString();
  } else if (finalAmt < 1) {
    amtStr = finalAmt.toFixed(2).replace(/\.0+$/, '');
  } else if (finalAmt < 10) {
    amtStr = finalAmt.toFixed(1).replace(/\.0+$/, '');
  } else {
    amtStr = Math.round(finalAmt).toString();
  }

  return { amountStr: amtStr, unitStr: finalUnit };
}